import { useState } from 'react';
import unescapedText from 'I18n/utils/unescapedText';
import { getInvoiceProperty } from 'invoices-ui-lib/utils/property';
import { Button } from 'foundations-components/transitional/components/Button';
import { DialogBody } from 'foundations-components/transitional/components/DialogBody';
import { DialogFooter } from 'foundations-components/transitional/components/DialogFooter';
import { FormControl } from 'foundations-components/transitional/components/FormControl';
import { WarningIcon } from 'foundations-assets';
import { FormattedIntlCurrency } from 'I18n/components/FormattedIntlCurrency';
import FormattedJSXMessage from 'I18n/components/FormattedJSXMessage';
import FormattedNumber from 'I18n/components/FormattedNumber';
import FormattedMessage from 'I18n/components/FormattedMessage';
import { Tooltip } from 'foundations-components/transitional/components/Tooltip';
import { SearchInput } from 'foundations-components/transitional/components/SearchInput';
import { ToggleGroup } from 'foundations-components/transitional/components/ToggleGroup';
import { LoadingSpinner } from 'foundations-components/transitional/components/LoadingSpinner';
import { Paginator } from 'foundations-components/transitional/components/Paginator';
import PaymentOption from './PaymentOption';
import useApplyPayments from '../../hooks/useApplyPayments';
import RecordPaymentTabs from './RecordPaymentTabs';
import { LoadingButton } from 'foundations-components/transitional/components/LoadingButton';
import { View } from 'foundations-components/components/View';
import { jsx as _jsx } from "react/jsx-runtime";
import { jsxs as _jsxs } from "react/jsx-runtime";
import { Fragment as _Fragment } from "react/jsx-runtime";
const ApplyExistingPaymentTab = props => {
  const {
    invoice,
    onSuccess,
    onFailure,
    onReject,
    selectedTab,
    setSelectedTab,
    shouldDisablePartialPayments
  } = props;
  const {
    currentPage,
    pageCount,
    total,
    balanceDue,
    balanceSelected,
    loadingQuery,
    loadingMutation,
    options,
    selections,
    selectionsToDisplay,
    handleOnSearch,
    handleOnSelect,
    handleOnPageChange,
    handleOnConfirm,
    isSelected
  } = useApplyPayments(invoice, onSuccess, onFailure);
  const [isLoading, setIsLoading] = useState(false);
  const currency = getInvoiceProperty(invoice, 'hs_currency');
  const selectedExceedsBalance = balanceSelected > balanceDue;
  const selectedIsLessThanBalance = balanceSelected < balanceDue;
  const shouldDisableConfirmButton = loadingQuery || loadingMutation || selections.length === 0 || selectedExceedsBalance || selectedIsLessThanBalance && shouldDisablePartialPayments;
  return /*#__PURE__*/_jsxs(_Fragment, {
    children: [/*#__PURE__*/_jsxs(DialogBody, {
      children: [/*#__PURE__*/_jsx(RecordPaymentTabs, {
        selectedTab: selectedTab,
        setSelectedTab: setSelectedTab,
        shouldDisableTabs: isLoading
      }), /*#__PURE__*/_jsx(FormControl, {
        paddingBlockStart: "400",
        children: /*#__PURE__*/_jsx(SearchInput, {
          "data-test-id": "record-existing-payment-search-input",
          onChange: event => handleOnSearch(event.target.value),
          placeholder: unescapedText('invoices.components.modals.applyExistingPayment.searchPlaceholder')
        })
      }), selectionsToDisplay.length > 0 ? /*#__PURE__*/_jsx(FormControl, {
        label: /*#__PURE__*/_jsx("div", {
          "data-test-id": "selections-label",
          children: /*#__PURE__*/_jsx(FormattedMessage, {
            message: "invoices.components.modals.applyExistingPayment.subheader.selected"
          })
        }),
        children: /*#__PURE__*/_jsx(ToggleGroup, {
          name: "separately-selected-payment-records",
          marginBlockStart: "400",
          children: selectionsToDisplay.map(option => /*#__PURE__*/_jsx(PaymentOption, {
            option: option,
            currencyCode: currency,
            balanceDue: balanceDue,
            selected: isSelected(option.id),
            onChange: handleOnSelect
          }, option.id))
        })
      }) : null, total === 0 ? /*#__PURE__*/_jsx(View, {
        marginBlockStart: "400",
        as: "div",
        children: /*#__PURE__*/_jsx(FormattedMessage, {
          message: "invoices.components.modals.applyExistingPayment.noResults"
        })
      }) : null, total && options.length > 0 ? /*#__PURE__*/_jsx(FormControl, {
        label: /*#__PURE__*/_jsx("div", {
          "data-test-id": "options-label",
          children: /*#__PURE__*/_jsx(FormattedJSXMessage, {
            message: "invoices.components.modals.applyExistingPayment.subheader.payments_jsx",
            elements: {
              FormattedNumber
            },
            options: {
              value: total
            }
          })
        }),
        children: /*#__PURE__*/_jsx(ToggleGroup, {
          name: "selected-payment-records",
          marginBlockStart: "400",
          children: options.map(option => /*#__PURE__*/_jsx(PaymentOption, {
            option: option,
            currencyCode: currency,
            balanceDue: balanceDue,
            selected: isSelected(option.id),
            onChange: handleOnSelect
          }, option.id))
        })
      }) : null, pageCount ? /*#__PURE__*/_jsx(Paginator, {
        pageCount: pageCount,
        showFirstLastButtons: false,
        onPageChange: event => handleOnPageChange(event.target.value),
        page: currentPage,
        marginBlockStart: "400"
      }) : null, loadingQuery ? /*#__PURE__*/_jsx(LoadingSpinner, {}) : null]
    }), /*#__PURE__*/_jsxs(DialogFooter, {
      children: [/*#__PURE__*/_jsx(Tooltip, {
        disabled: !shouldDisableConfirmButton,
        title: selectedExceedsBalance ? /*#__PURE__*/_jsx(FormattedMessage, {
          message: "invoices.components.modals.applyExistingPayment.confirmButton.exceedsBalance"
        }) : selectedIsLessThanBalance && shouldDisablePartialPayments ? /*#__PURE__*/_jsx(FormattedMessage, {
          message: "invoices.components.modals.applyExistingPayment.confirmButton.partialPayment"
        }) : null,
        children: /*#__PURE__*/_jsx(LoadingButton, {
          use: "primary",
          onClick: () => {
            setIsLoading(true);
            handleOnConfirm();
          },
          disabled: shouldDisableConfirmButton,
          "data-test-id": "add-existing-payment-confirm-button",
          loading: isLoading,
          children: /*#__PURE__*/_jsx(FormattedMessage, {
            message: "invoices.components.modals.applyExistingPayment.confirmButton.text",
            options: {
              count: selections.length > 1 ? 2 : 1
            }
          })
        })
      }), /*#__PURE__*/_jsx(Button, {
        onClick: onReject,
        disabled: isLoading,
        children: /*#__PURE__*/_jsx(FormattedMessage, {
          message: "invoices.components.modals.applyExistingPayment.cancelButton"
        })
      }), /*#__PURE__*/_jsxs("div", {
        children: [selectedExceedsBalance ? /*#__PURE__*/_jsxs("span", {
          children: [/*#__PURE__*/_jsx(WarningIcon, {
            color: "fill-alert-default",
            alignWithText: true
          }), ' ']
        }) : null, /*#__PURE__*/_jsx("span", {
          "data-test-id": "balance-due-message",
          children: /*#__PURE__*/_jsx(FormattedJSXMessage, {
            message: "invoices.components.modals.applyExistingPayment.balanceSelected_jsx",
            elements: {
              FormattedCurrency: FormattedIntlCurrency
            },
            options: {
              currencyCode: currency,
              valueSelected: balanceSelected,
              valueTotal: balanceDue
            }
          })
        })]
      })]
    })]
  });
};
export default ApplyExistingPaymentTab;